/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import { BrandingUiInfoKeys } from '../entities';
import { ErrorTypes, VodPlayActionError } from '../error';
import { VodInteractor } from '../interactors';
import { Locale, LocaleKeys } from '../locale';
import { Log } from '../logging';
import { PlaybackDataModelImpl } from '../playback/impls';
import { BrandingUiUtility } from '../utils';
import { ErrorCodeUtil } from '../utils/ErrorCodeUtil';
import { IErrorAdapter } from './adapters';

export class VodPlayActionErrorMapper {
    private static TAG: string = 'VodPlayActionErrorMapper';

    private code: string;

    private description: string;

    private message: string;

    private title: string;

    private type: string;

    private adapter: IErrorAdapter;

    public constructor(adapter: IErrorAdapter) {
        this.code = '';
        this.description = '';
        this.message = ''; // will update this based on requirement
        this.title = '';
        this.type = '';
        this.adapter = adapter;
    }

    private setGeneralErrorProperties(errorCode: string, actualError?: any) {
        // TODO: need to remove this dependency on VodInteractor and BrandingUiUtility
        const vodInteractor = new VodInteractor();
        switch (errorCode) {
            case ErrorTypes.VOD_PLAY_ACTION_RENTALS_NOT_ALLOWED_V233:
                this.code = errorCode;
                this.title = Locale.getString(LocaleKeys.VOD_OVERLAY_RENTAL_NOT_ALLOWED);
                this.description = Locale.getString(LocaleKeys.VOD_OVERLAY_CANNOT_RENT);
                break;
            case ErrorTypes.VOD_PLAY_ACTION_PURCHASE_FAILED_V361: {
                const selectedAction = PlaybackDataModelImpl.getInstance().getNewActionDetails();

                // Extract subcode once and reuse for both code and description
                const subcode = actualError
                    ? ErrorCodeUtil.extractSubErrorCode(actualError, this.adapter)
                    : '';

                this.code = subcode ? `${errorCode}-${subcode}` : errorCode;

                this.title = BrandingUiUtility.formatDescription(
                    Locale.getString(LocaleKeys.VOD_OVERLAY_PURCHASE_FAILED_TITLE),
                    {
                        VOD_NAME: selectedAction.providerDisplayName,
                    },
                );

                this.description = BrandingUiUtility.formatDescription(
                    Locale.getString(LocaleKeys.VOD_OVERLAY_PURCHASE_FAILED_DESCRIPTION),
                    {
                        ERROR_CODE: errorCode,
                        SUBCODE: subcode,
                    },
                );
                break;
            }
            case ErrorTypes.VOD_PLAY_ACTION_CANNOT_INITIATE_PROCESS_V431:
            case ErrorTypes.VOD_PLAY_ACTION_PROGRAM_UNKNOWN_ENTITLEMENT_V432:
            case ErrorTypes.VOD_PLAY_ACTION_PROGRAM_UNAVAILABLE_V433:
            default:
                this.title = Locale.getString(LocaleKeys.VOD_PLAY_ACTION_SHOW_UNAVAILABLE_TITLE);
                this.description = BrandingUiUtility.formatDescription(
                    Locale.getString(LocaleKeys.VOD_PLAY_ACTION_SHOW_UNAVAILABLE_DESCRIPTION),
                    {
                        ERROR_CODE: errorCode,
                        PHONE_CUSTOMER_SUPPORT_NUMBER: vodInteractor.getBrandingUiInfoForKey(
                            BrandingUiInfoKeys.PHONE_CUSTOMER_SUPPORT_NUMBER,
                        ),
                        CUSTOMER_SUPPORT_NAME: vodInteractor.getBrandingUiInfoForKey(
                            BrandingUiInfoKeys.CUSTOMER_SUPPORT_NAME,
                        ),
                    },
                );
                this.code = errorCode;
                break;
        }
    }

    public getErrorByCode(errorCode: string, actualError?: any): VodPlayActionError {
        this.setGeneralErrorProperties(errorCode, actualError);
        const error = new VodPlayActionError(
            this.code,
            this.message,
            this.description,
            this.title,
            this.type,
        );
        Log.e(VodPlayActionErrorMapper.TAG, 'error during play action', error);
        Diagnostics.error(VodPlayActionErrorMapper.TAG, 'error during play action', error);
        return error;
    }
}
